import React, { useState } from 'react';
import { GripVertical, Plus, X, Search } from 'lucide-react';

// Componente principal para gestionar secciones
const GestionSeccionesHome = ({ secciones, paquetes, onUpdateSeccion }) => {
  const [tabActiva, setTabActiva] = useState(0);
  const [modalAgregar, setModalAgregar] = useState(false);
  const [seccionActual, setSeccionActual] = useState(null);
  const [busquedaModal, setBusquedaModal] = useState('');
  const [paquetesSeleccionados, setPaquetesSeleccionados] = useState([]);

  // Filtrar solo paquetes activos
  const paquetesActivos = paquetes.filter(p => p.estado === 'activo');

  // Abrir modal para agregar paquetes
  const abrirModalAgregar = (seccion) => {
    setSeccionActual(seccion);
    setPaquetesSeleccionados([]);
    setBusquedaModal('');
    setModalAgregar(true);
  };

  // Agregar paquetes seleccionados a la sección
  const agregarPaquetes = async () => {
    if (!seccionActual || paquetesSeleccionados.length === 0) return;

    const nuevosIds = [
      ...(seccionActual.paquetesIds || []),
      ...paquetesSeleccionados
    ];

    await onUpdateSeccion(seccionActual.id, { paquetesIds: nuevosIds });
    setModalAgregar(false);
  };

  // Remover paquete de una sección
  const removerPaquete = async (seccionId, paqueteId) => {
    const seccion = secciones.find(s => s.id === seccionId);
    const nuevosIds = (seccion.paquetesIds || []).filter(id => id !== paqueteId);
    await onUpdateSeccion(seccionId, { paquetesIds: nuevosIds });
  };

  // Filtrar paquetes para el modal
  const paquetesFiltrados = paquetesActivos.filter(p => {
    if (!busquedaModal) return true;
    const search = busquedaModal.toLowerCase();
    return (
      p.titulo.toLowerCase().includes(search) ||
      p.destino_nombre?.toLowerCase().includes(search) ||
      p.hotel?.toLowerCase().includes(search)
    );
  });

  // ✅ NUEVO: Ordenar por fecha de salida (más próximos primero)
  const paquetesOrdenados = [...paquetesFiltrados].sort((a, b) => {
    if (!a.fecha_salida) return 1;
    if (!b.fecha_salida) return -1;
    return new Date(a.fecha_salida) - new Date(b.fecha_salida);
  });

  // Obtener paquetes que ya están asignados a la sección actual
  const paquetesYaAsignados = new Set(seccionActual?.paquetesIds || []);

  return (
    <div className="bg-white rounded-lg shadow-lg mt-8">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          📋 Gestión de Secciones en Home
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Los paquetes solo aparecen en las secciones si están en estado "activo"
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex overflow-x-auto">
          {secciones.map((seccion, index) => (
            <button
              key={seccion.id}
              onClick={() => setTabActiva(index)}
              className={`px-6 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                tabActiva === index
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {seccion.titulo || `Sección ${index + 1}`}
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100">
                {seccion.paquetesIds?.length || 0}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Contenido de la Tab Activa */}
      <div className="p-6">
        {secciones[tabActiva] && (
          <SeccionCard
            seccion={secciones[tabActiva]}
            paquetes={paquetes}
            onRemoverPaquete={removerPaquete}
            onAgregarPaquetes={() => abrirModalAgregar(secciones[tabActiva])}
            onUpdateSeccion={onUpdateSeccion}
          />
        )}
      </div>

      {/* Modal para Agregar Paquetes */}
      {modalAgregar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Header del Modal */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Agregar Paquetes a "{seccionActual?.titulo}"
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {paquetesSeleccionados.length} paquete(s) seleccionado(s)
                  </p>
                </div>
                <button
                  onClick={() => setModalAgregar(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Buscador */}
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar por título, destino o hotel..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  value={busquedaModal}
                  onChange={(e) => setBusquedaModal(e.target.value)}
                />
              </div>
            </div>

            {/* Lista de Paquetes - ✅ CON FECHA DE SALIDA */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {paquetesOrdenados.map(paquete => {
                  const yaAsignado = paquetesYaAsignados.has(paquete.id);
                  const seleccionado = paquetesSeleccionados.includes(paquete.id);
                  
                  // ✅ Formatear fecha de salida
                  const fechaSalida = paquete.fecha_salida 
                    ? new Date(paquete.fecha_salida).toLocaleDateString('es-AR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                    : 'Sin fecha';

                  return (
                    <label
                      key={paquete.id}
                      className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        yaAsignado
                          ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                          : seleccionado
                          ? 'bg-blue-50 border-blue-500'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={seleccionado}
                        disabled={yaAsignado}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPaquetesSeleccionados([...paquetesSeleccionados, paquete.id]);
                          } else {
                            setPaquetesSeleccionados(paquetesSeleccionados.filter(id => id !== paquete.id));
                          }
                        }}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{paquete.titulo}</span>
                          <span className={`text-sm font-semibold ${
                            paquete.moneda === 'U$S' ? 'text-blue-600' : 'text-green-600'
                          }`}>
                            {paquete.moneda || '$'} {paquete.tarifa?.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap">
                          {/* ✅ Fecha de salida - DESTACADA */}
                          <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                            📅 {fechaSalida}
                          </span>
                          
                          {paquete.destino_nombre && <span>📍 {paquete.destino_nombre}</span>}
                          {paquete.hotel && <span>• 🏨 {paquete.hotel}</span>}
                          
                          {yaAsignado && (
                            <span className="ml-auto bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              Ya asignado
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}

                {paquetesOrdenados.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>No se encontraron paquetes</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer del Modal */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setModalAgregar(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={agregarPaquetes}
                disabled={paquetesSeleccionados.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Agregar {paquetesSeleccionados.length > 0 && `(${paquetesSeleccionados.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para cada card de sección - ✅ CON FECHA DE SALIDA
const SeccionCard = ({ seccion, paquetes, onRemoverPaquete, onAgregarPaquetes, onUpdateSeccion }) => {
  const [editandoTitulo, setEditandoTitulo] = useState(false);
  const [nuevoTitulo, setNuevoTitulo] = useState(seccion.titulo);

  const paquetesAsignados = (seccion.paquetesIds || [])
    .map(id => paquetes.find(p => p.id === id))
    .filter(Boolean);

  const guardarTitulo = async () => {
    if (nuevoTitulo.trim() !== seccion.titulo) {
      await onUpdateSeccion(seccion.id, { titulo: nuevoTitulo });
    }
    setEditandoTitulo(false);
  };

  return (
    <div className="space-y-4">
      {/* Título de la Sección */}
      <div className="flex items-center gap-3">
        {editandoTitulo ? (
          <input
            type="text"
            value={nuevoTitulo}
            onChange={(e) => setNuevoTitulo(e.target.value)}
            onBlur={guardarTitulo}
            onKeyDown={(e) => e.key === 'Enter' && guardarTitulo()}
            autoFocus
            className="flex-1 px-4 py-2 text-lg font-semibold border-2 border-blue-500 rounded-lg focus:outline-none"
          />
        ) : (
          <h4
            onClick={() => setEditandoTitulo(true)}
            className="flex-1 text-lg font-semibold text-gray-900 cursor-pointer hover:text-blue-600"
          >
            {seccion.titulo}
          </h4>
        )}
        <button
          onClick={onAgregarPaquetes}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Agregar Paquetes
        </button>
      </div>

      {/* Lista de Paquetes Asignados - ✅ CON FECHA DE SALIDA */}
      {paquetesAsignados.length > 0 ? (
        <div className="grid grid-cols-1 gap-3">
          {paquetesAsignados.map((paquete) => {
            // ✅ Formatear fecha de salida
            const fechaSalida = paquete.fecha_salida 
              ? new Date(paquete.fecha_salida).toLocaleDateString('es-AR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })
              : 'Sin fecha';
            
            return (
              <div
                key={paquete.id}
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors group"
              >
                {/* Drag Handle */}
                <div className="text-gray-400 cursor-move">
                  <GripVertical size={20} />
                </div>

                {/* Información del Paquete */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h5 className="font-medium text-gray-900 truncate">{paquete.titulo}</h5>
                    <span className={`text-sm font-semibold whitespace-nowrap ${
                      paquete.moneda === 'U$S' ? 'text-blue-600' : 'text-green-600'
                    }`}>
                      {paquete.moneda || '$'} {paquete.tarifa?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap">
                    {/* ✅ Fecha de salida - DESTACADA */}
                    <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                      📅 {fechaSalida}
                    </span>
                    
                    {paquete.destino_nombre && <span>📍 {paquete.destino_nombre}</span>}
                    {paquete.hotel && <span>• 🏨 {paquete.hotel}</span>}
                    
                    <span className={`ml-auto px-2 py-0.5 rounded-full text-xs ${
                      paquete.estado === 'activo'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {paquete.estado}
                    </span>
                  </div>
                </div>

                {/* Botón Remover */}
                <button
                  onClick={() => onRemoverPaquete(seccion.id, paquete.id)}
                  className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={20} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500 mb-4">No hay paquetes asignados a esta sección</p>
          <button
            onClick={onAgregarPaquetes}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} />
            Agregar Primer Paquete
          </button>
        </div>
      )}

      {/* Información adicional */}
      <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
        <span>{paquetesAsignados.length} paquete(s) en esta sección</span>
        <span>Solo se muestran paquetes activos en el home</span>
      </div>
    </div>
  );
};

export default GestionSeccionesHome;